import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Switch,
  useMediaQuery,
  useTheme,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import type {
  CentralRiskEventBbox,
  CentralRiskEventPayload,
} from './centralRiskEventTypes';
import {
  buildEventDialogMessage,
  syncMessageWithBboxDetails,
  CENTRAL_RISK_DIALOG_Z,
  formatBboxAlertDetailLines,
} from './centralRiskEventTypes';
import { eventService } from '../../services/eventService';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';
import api from '../../services/api';
import {
  EventAlertDeviceControls,
  stopAllEventAlertDevices,
} from './EventAlertDeviceControls';
import {
  startPoolAlertDevices,
} from '../../services/poolAlertDeviceService';
import { mergeSystemNotificationSettings } from '../../constants/notificationSettings';
import {
  composeEventSnapshotWithBboxes,
  dataUrlToBase64,
  fetchAuthenticatedImageDataUrl,
  formatBboxOverlayLabel,
} from '../../utils/composeEventSnapshot';
import {
  resolveRiskAppearance,
  upsertRiskAppearanceEntry,
} from '../../constants/riskEventAppearance';
import {
  fetchPoolSafetyAlertChannels,
  poolEffectiveSafetyChannels,
} from '../../services/poolSafetyAlertChannels';
import {
  SAFETY_ALERT_CHANNEL_LABELS,
  type SafetyAlertChannelOverrides,
} from '../../constants/poolSafetyAlertChannels';
import EditableSnapshotBboxEditor from '../events/EditableSnapshotBboxEditor';
import { useEventContext } from '../../contexts/EventContext';
import { submitDetectionLabel } from '../../services/detectionLabelService';
import { startControlRoomSirenForPopup } from '../../services/controlRoomSirenService';

type AlertChannel = 'sms' | 'kakao' | 'wearable';

type Props = {
  open: boolean;
  payload: CentralRiskEventPayload | null;
  /** true면 이벤트 창 오픈 시 등록 채널로 알림 즉시 발송 */
  autoDispatchAlert?: boolean;
  onClose: () => void;
  onFalsePositive?: (payload: CentralRiskEventPayload) => void | Promise<void>;
  onEventRecorded?: (eventDbId: number) => void;
};

const CentralRiskEventDialog: React.FC<Props> = ({
  open,
  payload,
  autoDispatchAlert = false,
  onClose,
  onFalsePositive,
  onEventRecorded,
}) => {
  const { patchEventImageUrl } = useEventContext();
  const theme = useTheme();
  const isMobileDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertLevel, setAlertLevel] = useState<'warning' | 'danger'>('warning');
  const [alertText, setAlertText] = useState('');
  /** 팝업에 바로 보이는 전체 메시지(자동초안+이벤트문구+bbox) — 수정 가능 */
  const [editEventMessage, setEditEventMessage] = useState('');
  const eventMessageTouchedRef = useRef(false);
  const [alertPhones, setAlertPhones] = useState('');
  const [alertSending, setAlertSending] = useState(false);
  const [alertError, setAlertError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [autoDispatchNote, setAutoDispatchNote] = useState<string | null>(null);
  const [autoDispatchBusy, setAutoDispatchBusy] = useState(false);
  const [channels, setChannels] = useState<SafetyAlertChannelOverrides>({
    sms: true,
    kakao: false,
    wearable: true,
  });
  /** 시설 정보의 채널 on/off — 꺼진 매체는 선택 불가 */
  const [poolChannelAllowed, setPoolChannelAllowed] = useState<SafetyAlertChannelOverrides>({
    sms: true,
    kakao: false,
    wearable: true,
  });
  const [channelsPrefsLoading, setChannelsPrefsLoading] = useState(false);
  /** 알림 전송 시 이벤트 캡처를 API에 포함 (자동·수동 알림 공통) */
  const [includeSnapshotWithAlert, setIncludeSnapshotWithAlert] = useState(true);
  /** 위험/경고 알림 미리보기에서 함께 울릴 경광등·부저 */
  const [alertSirenOn, setAlertSirenOn] = useState(true);
  const [alertBuzzerOn, setAlertBuzzerOn] = useState(true);
  /** 이벤트 창에서 선택한 경광등/부저 대상 (기본 all) */
  const sirenTargetRef = useRef('');
  const buzzerTargetRef = useRef('');
  const sirenDeviceCountRef = useRef(0);
  const buzzerDeviceCountRef = useRef(0);
  const alertDevicesStartedRef = useRef(false);
  /** 관제 「닫기(시설 확인 대기)」 — 창만 닫고 경광 유지, N초 후 자동 OFF */
  const keepDevicesForFacilityRef = useRef(false);
  const facilityAckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const FACILITY_ACK_DEVICE_SEC = 15;
  /** 팝업이 열릴 때 잡은 스크린샷 — 알림 전송·eventDbId 변경과 무관하게 이미지만 유지 */
  const frozenSnapshotRef = useRef<string | null>(null);
  const [editSnapshotUrl, setEditSnapshotUrl] = useState<string | null>(null);
  const [editBboxes, setEditBboxes] = useState<CentralRiskEventBbox[]>([]);
  const [bboxesDirty, setBboxesDirty] = useState(false);
  const [bboxSaveBusy, setBboxSaveBusy] = useState(false);
  const [bboxSaveNote, setBboxSaveNote] = useState<string | null>(null);
  const [editRiskLabel, setEditRiskLabel] = useState('');
  const [editRiskDescription, setEditRiskDescription] = useState('');
  const [editBboxColor, setEditBboxColor] = useState('#ff1744');
  const [selectedBboxIndex, setSelectedBboxIndex] = useState<number | null>(null);
  const autoDispatchedKeyRef = useRef<string | null>(null);
  /** 알림 전송으로 이벤트가 저장된 경우 — 닫을 때 스테이징을 삭제하지 않음 */
  const alertPersistedRef = useRef(false);
  /** bbox를 스테이징에 저장한 경우 — 닫아도 캡처 스테이징 유지 */
  const stagingSnapshotKeptRef = useRef(false);
  /**
   * 이 팝업이 열린 동안 관제실 경광은 최초 1회만.
   * 전송 후 eventDbId 패치로 effect가 다시 돌며 재울림·재팝업 되는 것을 막음.
   */
  const controlRoomSirenStartedForOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      frozenSnapshotRef.current = null;
      setEditSnapshotUrl(null);
      setEditBboxes([]);
      setBboxesDirty(false);
      setBboxSaveNote(null);
      setIncludeSnapshotWithAlert(false);
      setEditEventMessage('');
      eventMessageTouchedRef.current = false;
      controlRoomSirenStartedForOpenRef.current = false;
      return;
    }
    alertPersistedRef.current = false;
    stagingSnapshotKeptRef.current = false;
    eventMessageTouchedRef.current = false;
    // 팝업 재오픈 시 캡처 포함 기본 ON (닫을 때 false로 리셋됨)
    setIncludeSnapshotWithAlert(true);
    const snap = payload?.imageDataUrl?.trim();
    if (snap?.startsWith('data:')) {
      frozenSnapshotRef.current = snap;
      setEditSnapshotUrl(snap);
    } else {
      frozenSnapshotRef.current = null;
      setEditSnapshotUrl(null);
    }
    if (Array.isArray(payload?.bboxes) && payload.bboxes.length > 0) {
      setEditBboxes(payload.bboxes.map((b) => ({ ...b, bbox: [...(b.bbox || [])] })));
    } else {
      setEditBboxes([]);
    }
    const app = resolveRiskAppearance(payload?.riskType, {
      label: payload?.riskLabel,
      color: payload?.bboxColor,
    });
    setEditRiskLabel('');
    setEditRiskDescription('');
    setEditBboxColor(payload?.bboxColor || app.color || '#ff1744');
    setSelectedBboxIndex(null);
    setBboxesDirty(false);
    const boxes = Array.isArray(payload?.bboxes) ? payload.bboxes : [];
    const seeded = boxes.find(
      (b) =>
        String(b.riskSubtypeLabel || '').trim() ||
        String(b.riskDescription || '').trim()
    );
    if (seeded) {
      setEditRiskLabel(String(seeded.riskSubtypeLabel || '').trim());
      setEditRiskDescription(String(seeded.riskDescription || '').trim());
      if (seeded.borderColor) setEditBboxColor(seeded.borderColor);
    }
    setEditEventMessage(
      buildEventDialogMessage(
        'danger',
        payload?.poolName || '',
        payload?.location || '',
        boxes,
        payload?.message
      )
    );

    // API 캡처 URL·저장된 bbox 메타 복원 (대시보드 최근 이벤트와 동일하게 보이게)
    const eventDbId = payload?.eventDbId;
    const imageHint = payload?.imageDataUrl?.trim() || '';
    let cancelled = false;
    void (async () => {
      try {
        if (imageHint && !imageHint.startsWith('data:')) {
          const dataUrl = await fetchAuthenticatedImageDataUrl(imageHint);
          if (!cancelled && dataUrl) {
            frozenSnapshotRef.current = dataUrl;
            setEditSnapshotUrl(dataUrl);
          }
        }
        if (eventDbId == null) return;
        const meta = await eventService.getRiskSnapshotMeta(eventDbId);
        if (cancelled) return;
        const metaBoxes = Array.isArray(meta.bboxes)
          ? (meta.bboxes as CentralRiskEventBbox[]).map((b) => ({
              ...b,
              bbox: [...(b.bbox || [])],
            }))
          : [];
        if (metaBoxes.length > 0) {
          // 페이로드에 이미 산 bbox(AI/수동)가 있으면 메타로 덮지 않음.
          // 예전에는 메타로 메시지 sync → 코드명·설명이 지워지는 버그가 있었음.
          const hadPayloadBoxes =
            Array.isArray(payload?.bboxes) && payload.bboxes.length > 0;
          setEditBboxes((prev) => (prev.length > 0 ? prev : metaBoxes));
          if (!hadPayloadBoxes) {
            const first = metaBoxes.find(
              (b) =>
                String(b.riskSubtypeLabel || '').trim() ||
                String(b.riskDescription || '').trim()
            );
            if (first) {
              setEditRiskLabel(String(first.riskSubtypeLabel || '').trim());
              setEditRiskDescription(String(first.riskDescription || ''));
              if (first.borderColor) setEditBboxColor(first.borderColor);
            }
            if (!eventMessageTouchedRef.current) {
              setEditEventMessage((prev) =>
                syncMessageWithBboxDetails(prev, metaBoxes, payload?.message)
              );
            }
          }
        }
        if (!frozenSnapshotRef.current) {
          const rawUrl = `${meta.raw_image_url || meta.image_url}?t=${Date.now()}`;
          const dataUrl = await fetchAuthenticatedImageDataUrl(rawUrl);
          if (!cancelled && dataUrl) {
            frozenSnapshotRef.current = dataUrl;
            setEditSnapshotUrl(dataUrl);
          }
        }
      } catch {
        /* 메타 없음 — 캡처 없는 이벤트일 수 있음 */
      }
    })();

    return () => {
      cancelled = true;
    };
    // payload 식별자가 바뀌면(큐에서 다음 시설) 캡처를 그 이벤트 것으로 다시 고정
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identity, not every payload field
  }, [
    open,
    payload?.poolId,
    payload?.cctvId,
    payload?.triggerTs,
    payload?.riskType,
  ]);

  /** 이벤트 팝업이 뜨는 순간 관제실 경광 1회 — 전송·eventDbId 갱신 시 재호출 금지 */
  useEffect(() => {
    if (!open || !payload) return;
    if (controlRoomSirenStartedForOpenRef.current) return;
    controlRoomSirenStartedForOpenRef.current = true;
    const src = payload.source || 'ai';
    void startControlRoomSirenForPopup({
      pool_id: Number(payload.poolId),
      pool_name: payload.poolName || '',
      event_type: payload.riskType || '',
      risk_level: '위험',
      event_id: payload.eventDbId ?? null,
      source: src,
      message:
        src === 'test'
          ? `수동 이벤트 — ${payload.poolName || '시설'}`
          : `AI 감지 이벤트 — ${payload.poolName || '시설'}`,
    }).catch(() => {
      /* 장비 없음·비활성 등은 무시 — 이벤트 팝업은 유지 */
    });
    // eventDbId는 전송 후 패치되므로 deps에 넣지 않음 (재울림 원인)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open 세션당 1회
  }, [open, payload?.poolId, payload?.cctvId, payload?.triggerTs, payload?.riskType]);

  const effectiveRiskType = String(payload?.riskType || '').trim() || 'motion_risk';
  const effectiveRiskLabel = editRiskLabel.trim();
  const effectiveBboxColor = editBboxColor.trim() || '#ff1744';

  const applyTargetIndex = useMemo(() => {
    if (
      selectedBboxIndex != null &&
      selectedBboxIndex >= 0 &&
      selectedBboxIndex < editBboxes.length
    ) {
      return selectedBboxIndex;
    }
    if (editBboxes.length > 0) return editBboxes.length - 1;
    return null;
  }, [selectedBboxIndex, editBboxes.length]);

  /**
   * 입력란에만 있고 「코드명 적용」을 안 누른 값을 선택(또는 마지막) bbox에 반영.
   * 미리보기·전송 직전에 호출해 메시지에 빠지지 않게 함.
   */
  const flushPendingBboxFields = useCallback((): CentralRiskEventBbox[] => {
    const idx = applyTargetIndex;
    if (idx == null || idx < 0 || idx >= editBboxes.length) return editBboxes;
    const label = effectiveRiskLabel.trim();
    const desc = editRiskDescription.trim();
    if (!label && !desc) return editBboxes;
    const cur = editBboxes[idx];
    const curLabel = String(cur.riskSubtypeLabel || '').trim();
    const curDesc = String(cur.riskDescription || '').trim();
    const nextLabel = label || curLabel;
    const color = effectiveBboxColor;
    if (
      curLabel === nextLabel &&
      curDesc === desc &&
      (!label || String(cur.borderColor || '').trim() === color)
    ) {
      return editBboxes;
    }
    const next = editBboxes.map((b, i) =>
      i === idx
        ? {
            ...b,
            motionRisk: true,
            riskSubtypeLabel: nextLabel,
            riskDescription: desc,
            borderColor: label ? color : b.borderColor || color,
          }
        : b
    );
    setEditBboxes(next);
    setSelectedBboxIndex(idx);
    setBboxesDirty(true);
    return next;
  }, [
    applyTargetIndex,
    effectiveRiskLabel,
    editRiskDescription,
    effectiveBboxColor,
    editBboxes,
  ]);

  /** 코드명·설명·색 → 선택(또는 마지막) bbox 하나에만 적용 */
  const applyCodeNameAndColor = useCallback(() => {
    const idx = applyTargetIndex;
    if (idx == null) {
      setBboxSaveNote('먼저 bbox를 그린 뒤 적용');
      return;
    }
    const label = effectiveRiskLabel;
    const desc = editRiskDescription.trim();
    const color = effectiveBboxColor;
    if (!label) {
      setBboxSaveNote('코드명을 입력');
      return;
    }
    const next = editBboxes.map((b, i) =>
      i === idx
        ? {
            ...b,
            motionRisk: true,
            riskSubtypeLabel: label,
            riskDescription: desc,
            borderColor: color,
          }
        : b
    );
    setEditBboxes(next);
    setSelectedBboxIndex(idx);
    setBboxesDirty(true);
    setEditEventMessage((prev) =>
      syncMessageWithBboxDetails(prev, next, payload?.message)
    );
    setBboxSaveNote(`#${idx + 1} bbox에 코드명·설명·색 적용`);
  }, [
    applyTargetIndex,
    effectiveRiskLabel,
    editRiskDescription,
    effectiveBboxColor,
    editBboxes,
    payload?.message,
  ]);

  /** 내보내기: 각 bbox에 이미 적용된 값만 사용 (전역 덮어쓰기 없음) */
  const bboxesForExport = useMemo(() => editBboxes, [editBboxes]);

  const poolIdNumeric = useMemo(() => {
    if (payload?.poolId == null || !Number.isFinite(Number(payload.poolId))) {
      return null;
    }
    return Number(payload.poolId);
  }, [payload?.poolId]);

  useEffect(() => {
    if (!open) {
      setAlertOpen(false);
      setAlertError(null);
      setAutoDispatchNote(null);
      setAutoDispatchBusy(false);
      setActionBusy(false);
    }
  }, [open]);

  const clearFacilityAckTimer = useCallback(() => {
    if (facilityAckTimerRef.current) {
      clearTimeout(facilityAckTimerRef.current);
      facilityAckTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    // 새 이벤트 창이 열리면 이전 「시설 확인 대기」 타이머 취소 (새 울림을 끊지 않음)
    if (open) {
      clearFacilityAckTimer();
      keepDevicesForFacilityRef.current = false;
    }
  }, [open, clearFacilityAckTimer]);

  useEffect(() => {
    if (open || poolIdNumeric == null) return;
    // 시설 확인 대기 모드: 즉시 OFF 하지 않음 (타이머 또는 시설 「닫기」가 처리)
    if (keepDevicesForFacilityRef.current) return;
    void stopAllEventAlertDevices(poolIdNumeric).catch(() => undefined);
  }, [open, poolIdNumeric]);

  useEffect(() => {
    return () => clearFacilityAckTimer();
  }, [clearFacilityAckTimer]);

  const loadChannelDefaults = useCallback(async (): Promise<SafetyAlertChannelOverrides> => {
    if (payload?.poolId != null && Number.isFinite(Number(payload.poolId))) {
      const ch = await fetchPoolSafetyAlertChannels(Number(payload.poolId));
      const allowed = poolEffectiveSafetyChannels(ch);
      setPoolChannelAllowed(allowed);
      setChannels({ ...allowed });
      return allowed;
    }
    const response = await api.get<{ notifications?: Record<string, unknown> }>(
      '/api/settings/system'
    );
    const notifications = mergeSystemNotificationSettings(
      response?.notifications || undefined
    );
    const allowed = {
      sms: notifications.safety_channel_sms,
      kakao: notifications.safety_channel_kakao,
      wearable: notifications.safety_channel_wearable,
    };
    setPoolChannelAllowed(allowed);
    setChannels({ ...allowed });
    return allowed;
  }, [payload?.poolId]);

  /** 팝업 캡처를 알림 요청에 실어 시설 팝업·스테이징·이벤트에 남김 */
  const buildAlertSnapshotPayload = useCallback(
    async (
      boxesOverride?: CentralRiskEventBbox[]
    ): Promise<{
      snapshotBase64?: string;
      snapshotRawBase64?: string;
      bboxes?: CentralRiskEventBbox[];
    }> => {
      let raw =
        editSnapshotUrl?.trim() ||
        frozenSnapshotRef.current?.trim() ||
        payload?.imageDataUrl?.trim() ||
        '';
      if (!raw) return {};
      // API 경로만 있으면 data URL로 로드 — 없으면 시설 팝업에 사진이 안 감
      if (!raw.startsWith('data:')) {
        const fetched = await fetchAuthenticatedImageDataUrl(raw);
        if (!fetched?.startsWith('data:')) return {};
        raw = fetched;
        frozenSnapshotRef.current = fetched;
        setEditSnapshotUrl(fetched);
      }
      const bboxes =
        boxesOverride && boxesOverride.length > 0
          ? boxesOverride
          : bboxesForExport;
      try {
        const composed =
          bboxes.length > 0
            ? await composeEventSnapshotWithBboxes(raw, bboxes)
            : raw;
        const snapshotBase64 =
          dataUrlToBase64(composed) ||
          (composed.startsWith('data:') ? undefined : composed);
        const snapshotRawBase64 =
          dataUrlToBase64(raw) || (raw.startsWith('data:') ? undefined : raw);
        return {
          snapshotBase64,
          snapshotRawBase64,
          bboxes: bboxes.length > 0 ? bboxes : undefined,
        };
      } catch {
        const snapshotBase64 =
          dataUrlToBase64(raw) || (raw.startsWith('data:') ? undefined : raw);
        return {
          snapshotBase64,
          snapshotRawBase64: snapshotBase64,
          bboxes: bboxes.length > 0 ? bboxes : undefined,
        };
      }
    },
    [payload?.imageDataUrl, bboxesForExport, editSnapshotUrl]
  );

  useEffect(() => {
    if (!open || !payload || !autoDispatchAlert) return;
    const key = `${payload.poolId}:${payload.cctvId ?? 0}:${payload.riskType}:${payload.triggerTs ?? ''}`;
    if (autoDispatchedKeyRef.current === key) return;

    const poolId = payload.poolId;
    const poolName = payload.poolName;
    const location = payload.location;
    const riskType = effectiveRiskType;
    const cctvId = payload.cctvId;
    const triggerTs = payload.triggerTs;
    const safetyPhoneRaw = payload.safetyPhoneRaw || '';
    const draftBboxes =
      editBboxes.length > 0 ? editBboxes : payload.bboxes || [];

    let cancelled = false;
    let sent = false;
    setAutoDispatchBusy(true);
    setAutoDispatchNote(null);

    void (async () => {
      try {
        let allowed: SafetyAlertChannelOverrides;
        try {
          allowed = await loadChannelDefaults();
        } catch {
          allowed = { sms: true, kakao: false, wearable: true };
          setPoolChannelAllowed(allowed);
          setChannels(allowed);
        }
        if (cancelled) return;

        const selected: AlertChannel[] = [];
        if (allowed.sms) selected.push('sms');
        if (allowed.kakao) selected.push('kakao');
        if (allowed.wearable) selected.push('wearable');

        const level: 'warning' | 'danger' = 'danger';
        const text = buildEventDialogMessage(
          level,
          poolName,
          location,
          draftBboxes,
          payload?.message
        );
        const phoneList = safetyPhoneRaw
          .split(/[,;\n]+/)
          .map((s) => s.trim())
          .filter(Boolean);

        // 채널이 없어도 호출 — 증거·캡처 이벤트 저장 + 미발송 사유 이력
        const snapPayload = includeSnapshotWithAlert
          ? await buildAlertSnapshotPayload()
          : {};
        if (payload?.cctvId != null && payload?.triggerTs != null && snapPayload.snapshotBase64) {
          try {
            await eventService.uploadStagedEvidenceSnapshot({
              poolId: payload.poolId,
              cctvId: Number(payload.cctvId),
              triggerTs: Number(payload.triggerTs),
              snapshotBase64: snapPayload.snapshotBase64,
              snapshotRawBase64: snapPayload.snapshotRawBase64,
              bboxes: snapPayload.bboxes || bboxesForExport,
            });
          } catch {
            /* 스테이징 실패해도 알림 body 캡처로 저장 */
          }
        }
        const res = await eventService.sendPoolSafetyAlert({
          poolId,
          message: text,
          phoneNumbers: phoneList.length > 0 ? phoneList : undefined,
          channels: selected,
          alertLevel: level,
          cctvId,
          location,
          riskType,
          triggerTs,
          snapshotBase64: snapPayload.snapshotBase64,
          snapshotRawBase64: snapPayload.snapshotRawBase64,
          bboxes: snapPayload.bboxes,
          // 팝업 오픈 시 이미 관제실 경광 시작 — 전송으로 재울림 방지
          skipControlRoomSiren: true,
        });
        if (cancelled) return;
        sent = true;
        autoDispatchedKeyRef.current = key;
        const recordedId = res?.event_id;
        if (recordedId != null && Number.isFinite(Number(recordedId))) {
          alertPersistedRef.current = true;
          const prevId = payload?.eventDbId;
          onEventRecorded?.(Number(recordedId));
          if (
            prevId != null &&
            Number.isFinite(Number(prevId)) &&
            Number(prevId) !== Number(recordedId)
          ) {
            try {
              await eventService.ackEvent(Number(prevId), false, '자동 알림 후 이전 이벤트 처리');
            } catch {
              /* ignore */
            }
          }
        }
        if (selected.length === 0) {
          setAutoDispatchNote(
            '자동 알림: 보낼 매체가 없어 외부 알림은 생략함. 증거는 이벤트·발송 이력에 기록됨.'
          );
        } else if (res?.success) {
          setAutoDispatchNote(
            res.partial
              ? '자동 알림을 발송함. 일부 채널은 실패 — 발송 이력을 확인.'
              : '자동 알림을 발송함'
          );
        } else {
          setAutoDispatchNote(
            `자동 알림을 시도함 ${
              res?.detail || '채널 실패 사유는 발송 이력에서 확인.'
            }`
          );
        }
      } catch (e: unknown) {
        if (cancelled) return;
        setAutoDispatchNote(
          formatApiErrorMessage(e, '자동 알림 발송에 실패')
        );
      } finally {
        if (!cancelled) setAutoDispatchBusy(false);
      }
    })();

    return () => {
      cancelled = true;
      if (!sent && autoDispatchedKeyRef.current === key) {
        autoDispatchedKeyRef.current = null;
      }
    };
    // payload 객체 식별자 변경에 재실행되지 않도록 핵심 필드만 의존
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-send once per event key
  }, [
    open,
    autoDispatchAlert,
    payload?.poolId,
    payload?.cctvId,
    payload?.riskType,
    payload?.triggerTs,
    loadChannelDefaults,
    buildAlertSnapshotPayload,
    includeSnapshotWithAlert,
  ]);

  const setChannelChecked = (key: AlertChannel, checked: boolean) => {
    if (!poolChannelAllowed[key]) return;
    setChannels((c) => ({ ...c, [key]: checked }));
  };

  const openAlertPreview = useCallback(
    async (level: 'warning' | 'danger') => {
      if (!payload) return;
      setAlertLevel(level);
      // 입력란만 채우고 적용 안 한 경우·이벤트 종류 선택만 한 경우도 메시지에 반영
      const boxes = flushPendingBboxFields();
      const base =
        editEventMessage.trim() ||
        buildEventDialogMessage(
          level,
          payload.poolName,
          payload.location,
          boxes,
          payload.message
        );
      const draft = syncMessageWithBboxDetails(base, boxes, payload.message);
      // 레벨 태그(<위험>/<경고>)만 교체
      const leveled = draft.replace(/^<(위험|경고)>/, level === 'danger' ? '<위험>' : '<경고>');
      setAlertText(leveled);
      setEditEventMessage(leveled);
      setAlertPhones((payload.safetyPhoneRaw || '').trim());
      setAlertError(null);
      setChannelsPrefsLoading(true);
      try {
        await loadChannelDefaults();
      } catch {
        const fallback = { sms: true, kakao: false, wearable: true };
        setPoolChannelAllowed(fallback);
        setChannels(fallback);
        setAlertError('시설 알림 채널 설정을 불러오기 실패');
      } finally {
        setChannelsPrefsLoading(false);
      }
      setAlertOpen(true);
      setAlertSirenOn(true);
      setAlertBuzzerOn(true);
    },
    [payload, loadChannelDefaults, editEventMessage, flushPendingBboxFields]
  );

  const closeAlertDialog = () => {
    setAlertOpen(false);
    setAlertError(null);
  };

  const stopAlertDevicesNow = useCallback(() => {
    if (poolIdNumeric == null) return;
    alertDevicesStartedRef.current = false;
    keepDevicesForFacilityRef.current = false;
    clearFacilityAckTimer();
    void stopAllEventAlertDevices(poolIdNumeric).catch(() => undefined);
  }, [poolIdNumeric, clearFacilityAckTimer]);

  const startAlertDevicesIfChecked = useCallback(async () => {
    if (poolIdNumeric == null) return;
    const jobs: Promise<unknown>[] = [];
    if (alertSirenOn && sirenDeviceCountRef.current > 0) {
      const target = (sirenTargetRef.current || '').trim();
      if (target) {
        jobs.push(
          startPoolAlertDevices({
            poolId: poolIdNumeric,
            deviceType: 'siren',
            target,
            alertLevel: alertLevel === 'danger' ? 'danger' : 'warning',
          })
        );
      }
    }
    if (alertBuzzerOn && buzzerDeviceCountRef.current > 0) {
      const target = (buzzerTargetRef.current || '').trim();
      if (target) {
        jobs.push(
          startPoolAlertDevices({
            poolId: poolIdNumeric,
            deviceType: 'buzzer',
            target,
            alertLevel: alertLevel === 'danger' ? 'danger' : 'warning',
          })
        );
      }
    }
    if (jobs.length === 0) return;
    const settled = await Promise.allSettled(jobs);
    const anyOk = settled.some(
      (s) => s.status === 'fulfilled' && (s.value as { success?: boolean })?.success
    );
    if (anyOk) {
      alertDevicesStartedRef.current = true;
    } else {
      const err = settled.find((s) => s.status === 'rejected') as PromiseRejectedResult | undefined;
      const msg = String(err?.reason?.message || err?.reason || '');
      // 등록 장비 없음은 무시 (울림 체크만 된 경우)
      if (!/등록된 장비/.test(msg)) {
        console.warn('alert devices start failed', err?.reason || settled);
      }
    }
  }, [poolIdNumeric, alertSirenOn, alertBuzzerOn, alertLevel]);

  /** DB 이벤트를 처리완료(ack) — 대시보드 「처리 중」에서 제외 */
  const ackEventInBackground = useCallback(
    async (
      snap: CentralRiskEventPayload,
      falsePositive: boolean,
      note: string
    ) => {
      if (onFalsePositive && falsePositive) {
        await onFalsePositive(snap);
        return;
      }
      if (snap.eventDbId == null && !snap.uiEventId) return;
      const ackId = eventService.resolveAckId({
        ack_id: snap.eventDbId,
        id: snap.uiEventId ?? snap.eventDbId,
      });
      if (ackId === '' || ackId == null) return;
      await eventService.ackEvent(ackId, falsePositive, note);
    },
    [onFalsePositive]
  );

  const discardStagingInBackground = useCallback(
    async (snap: CentralRiskEventPayload) => {
      if (alertPersistedRef.current || stagingSnapshotKeptRef.current) return;
      const cctvId = snap.cctvId;
      const triggerTs = snap.triggerTs;
      if (cctvId == null || triggerTs == null) return;
      try {
        await eventService.discardStagedEvidence({
          poolId: snap.poolId,
          cctvId: Number(cctvId),
          triggerTs: Number(triggerTs),
        });
      } catch {
        /* ignore */
      }
    },
    []
  );

  const finishClose = useCallback(
    (opts?: { keepDevicesForFacility?: boolean }) => {
      // API(경광등 정지 등) 완료를 기다리면 장비가 응답 없을 때 팝업이 영구히 안 닫힘 → 즉시 닫기
      const keep = opts?.keepDevicesForFacility === true;
      keepDevicesForFacilityRef.current = keep;
      onClose();
      if (keep) {
        // 시설 「닫기」가 그 전에 stop 하면 서버 쪽에서 이미 OFF. 여기서는 상한만 보장.
        clearFacilityAckTimer();
        const pid = poolIdNumeric;
        if (pid != null) {
          facilityAckTimerRef.current = setTimeout(() => {
            facilityAckTimerRef.current = null;
            keepDevicesForFacilityRef.current = false;
            alertDevicesStartedRef.current = false;
            void stopAllEventAlertDevices(pid).catch(() => undefined);
          }, FACILITY_ACK_DEVICE_SEC * 1000);
        }
      } else {
        stopAlertDevicesNow();
      }
    },
    [onClose, stopAlertDevicesNow, clearFacilityAckTimer, poolIdNumeric]
  );

  const selectedChannels = useMemo(() => {
    const list: AlertChannel[] = [];
    if (poolChannelAllowed.sms && channels.sms) list.push('sms');
    if (poolChannelAllowed.kakao && channels.kakao) list.push('kakao');
    if (poolChannelAllowed.wearable && channels.wearable) list.push('wearable');
    return list;
  }, [channels, poolChannelAllowed]);

  const bboxHistoryLines = useMemo(
    () => formatBboxAlertDetailLines(editBboxes),
    [editBboxes]
  );

  const snapshotUrlForEdit =
    editSnapshotUrl ||
    frozenSnapshotRef.current ||
    payload?.imageDataUrl?.trim() ||
    null;

  const previewTitle =
    alertLevel === 'danger' ? '위험알림 미리보기' : '경고알림 미리보기';

  /**
   * 오탐 → 이벤트 ack + 학습용 detection_labels(프레임·bbox) 저장.
   * 닫기 전에 스냅샷/박스를 캡처해야 useEffect 초기화와 레이스하지 않음.
   */
  const saveFpTrainingLabels = useCallback(
    async (
      snap: CentralRiskEventPayload,
      boxes: CentralRiskEventBbox[],
      frameDataUrl: string
    ) => {
      const cctvId = snap.cctvId;
      if (cctvId == null) return;

      let frameHint = (frameDataUrl || '').trim();
      if (frameHint && !frameHint.startsWith('data:')) {
        frameHint = (await fetchAuthenticatedImageDataUrl(frameHint)) || '';
      }
      const frame_image_base64 = frameHint.startsWith('data:')
        ? dataUrlToBase64(frameHint) || undefined
        : undefined;

      const note = `관제 이벤트 오탐${snap.riskType ? ` (${snap.riskType})` : ''}`;
      const validBoxes = boxes.filter(
        (b) =>
          Array.isArray(b.bbox) &&
          b.bbox.length >= 4 &&
          b.bbox.slice(0, 4).every((n) => Number.isFinite(Number(n)))
      );

      const jobs: Promise<unknown>[] = [];
      if (validBoxes.length > 0) {
        for (const b of validBoxes) {
          const bb = b.bbox.slice(0, 4).map(Number) as [
            number,
            number,
            number,
            number,
          ];
          jobs.push(
            submitDetectionLabel({
              pool_id: snap.poolId,
              cctv_id: Number(cctvId),
              label_type: 'false_positive',
              bbox: bb,
              track_id: b.trackId,
              class_name: b.class_name || b.riskSubtypeLabel || snap.riskType,
              note,
              frame_width: b.frame_width,
              frame_height: b.frame_height,
              frame_image_base64,
            })
          );
        }
      } else if (frame_image_base64) {
        // 박스 없어도 프레임만 hard-negative로 저장
        jobs.push(
          submitDetectionLabel({
            pool_id: snap.poolId,
            cctv_id: Number(cctvId),
            label_type: 'false_positive',
            note,
            frame_image_base64,
          })
        );
      }
      if (jobs.length === 0) return;
      const settled = await Promise.allSettled(jobs);
      const failed = settled.filter((s) => s.status === 'rejected');
      if (failed.length > 0) {
        console.warn('오탐 학습 라벨 저장 일부 실패', failed);
      }
    },
    []
  );

  const handleFalsePositive = () => {
    if (actionBusy || !payload) return;
    setActionBusy(true);
    setAlertError(null);

    const snap = payload;
    const boxesSnap = editBboxes.map((b) => ({
      ...b,
      bbox: Array.isArray(b.bbox) ? [...b.bbox] : [],
    }));
    const frameSnap =
      (editSnapshotUrl || frozenSnapshotRef.current || snap.imageDataUrl || '').trim();
    const shouldDiscard = !alertPersistedRef.current;

    finishClose();

    void (async () => {
      try {
        // 학습 라벨을 스테이징 discard보다 먼저 (프레임 확보)
        await saveFpTrainingLabels(snap, boxesSnap, frameSnap);
        if (shouldDiscard) await discardStagingInBackground(snap);
        await ackEventInBackground(snap, true, '관제 오탐');
      } catch (e) {
        console.error(e);
      }
    })();
  };

  const handleSendAlert = async () => {
    if (!payload) return;
    // 미리보기 이후에도 입력란을 고쳤을 수 있음 — 전송 직전 한 번 더 동기화
    const boxes = flushPendingBboxFields();
    const synced = syncMessageWithBboxDetails(
      alertText.trim() || editEventMessage.trim(),
      boxes,
      payload.message
    );
    const leveled = synced.replace(
      /^<(위험|경고)>/,
      alertLevel === 'danger' ? '<위험>' : '<경고>'
    );
    if (leveled !== alertText) {
      setAlertText(leveled);
      setEditEventMessage(leveled);
    }
    const text = leveled.trim();
    if (!text) {
      setAlertError('메시지를 입력');
      return;
    }
    if (alertSirenOn && sirenDeviceCountRef.current > 0 && !(sirenTargetRef.current || '').trim()) {
      setAlertError(
        '경광등 울림이 켜져 있음. 이벤트 창에서 울릴 경광등을 선택한 뒤 다시 전송.'
      );
      return;
    }
    if (alertBuzzerOn && buzzerDeviceCountRef.current > 0 && !(buzzerTargetRef.current || '').trim()) {
      setAlertError(
        '부저 울림이 켜져 있음. 이벤트 창에서 울릴 부저를 선택한 뒤 다시 전송.'
      );
      return;
    }
    // 채널·장비가 없어도 발송 요청 — 증거 이벤트 저장 + 미발송 사유 이력
    setAlertSending(true);
    setAlertError(null);
    try {
      const phoneList = alertPhones
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      // 편집한 bbox·캡처 → 시설 팝업·이벤트 저장 (캡처 포함 스위치 ON일 때)
      const snapPayload = includeSnapshotWithAlert
        ? await buildAlertSnapshotPayload(boxes)
        : {};
      if (payload.cctvId != null && payload.triggerTs != null && snapPayload.snapshotBase64) {
        try {
          await eventService.uploadStagedEvidenceSnapshot({
            poolId: payload.poolId,
            cctvId: Number(payload.cctvId),
            triggerTs: Number(payload.triggerTs),
            snapshotBase64: snapPayload.snapshotBase64,
            snapshotRawBase64: snapPayload.snapshotRawBase64,
            bboxes: snapPayload.bboxes || boxes,
          });
        } catch {
          /* body 캡처로 폴백 */
        }
      }
      const riskFromBox = boxes.find(
        (b) =>
          b.motionRisk &&
          b.class_name &&
          !['person', 'swimmer', 'lifeguard'].includes(String(b.class_name).toLowerCase())
      );
      // 경광등/부저는 알림 API와 병렬 — 알림 끝난 뒤에 보내면 늦거나 실패가 묻힘
      const alertDevicesPromise = startAlertDevicesIfChecked();
      const res = await eventService.sendPoolSafetyAlert({
        poolId: payload.poolId,
        message: text,
        phoneNumbers: phoneList.length > 0 ? phoneList : undefined,
        channels: selectedChannels,
        alertLevel: alertLevel,
        cctvId: payload.cctvId,
        location: payload.location,
        riskType: riskFromBox?.class_name || payload.riskType,
        triggerTs: payload.triggerTs,
        snapshotBase64: snapPayload.snapshotBase64,
        snapshotRawBase64: snapPayload.snapshotRawBase64,
        bboxes: snapPayload.bboxes,
        // 팝업 오픈 시 이미 관제실 경광 시작 — 끈 뒤 전송해도 재울림·재팝업 없음
        skipControlRoomSiren: true,
      });
      await alertDevicesPromise;      const prevEventDbId = payload.eventDbId;
      const recordedId = res?.event_id;
      alertPersistedRef.current = true;
      if (recordedId != null && Number.isFinite(Number(recordedId))) {
        onEventRecorded?.(Number(recordedId));
        setBboxesDirty(false);
        // 알림으로 새 이벤트가 생기면 이전 미해결 이벤트도 처리완료 처리
        if (
          prevEventDbId != null &&
          Number.isFinite(Number(prevEventDbId)) &&
          Number(prevEventDbId) !== Number(recordedId)
        ) {
          try {
            await eventService.ackEvent(Number(prevEventDbId), false, '알림 전송 후 이전 이벤트 처리');
          } catch (ePrev) {
            console.warn('previous event ack failed', ePrev);
          }
        }
      }
      setAlertOpen(false);
      const shareNote = res?.share_image_filename
        ? `공유 이미지 생성: ${res.share_image_filename}` +
          (res.share_image_folder_opened ? ' (폴더 열림)' : '')
        : null;
      const kakaoNote =
        res?.kakao_bot_ok === true
          ? '카카오 봇 보내기 시도 완료'
          : res?.kakao_bot_detail
            ? `카카오 봇: ${res.kakao_bot_detail}`
            : null;
      // 채널 실패·미발송은 중단하지 않음 — 사유는 발송 이력에 남김
      if (selectedChannels.length === 0 || (res?.accepted && !res?.success)) {
        setAlertError(
          [
            res?.detail ||
              '외부 알림 매체가 없어 발송하지 않음. 증거는 이벤트·발송 이력에 기록됨.',
            shareNote,
            kakaoNote,
          ]
            .filter(Boolean)
            .join(' · ')
        );
      } else if (res?.partial) {
        setAlertError(
          [
            res?.detail ||
              '일부 채널 발송에 실패. 알림 발송 이력에서 사유를 확인.',
            shareNote,
            kakaoNote,
          ]
            .filter(Boolean)
            .join(' · ')
        );
      } else if (shareNote || kakaoNote) {
        setAlertError([shareNote, kakaoNote].filter(Boolean).join(' · '));
      } else {
        setAlertError(null);
      }
    } catch (e: unknown) {
      setAlertError(formatApiErrorMessage(e, '알림 전송에 실패'));
    } finally {
      setAlertSending(false);
    }
  };

  const persistEditedSnapshot = useCallback(
    async (eventDbId: number | string): Promise<string | null> => {
      const snapPayload = await buildAlertSnapshotPayload();
      if (!snapPayload.snapshotBase64) return null;
      const res = await eventService.updateRiskSnapshot({
        eventId: eventDbId,
        snapshotBase64: snapPayload.snapshotBase64,
        snapshotRawBase64: snapPayload.snapshotRawBase64,
        bboxes: bboxesForExport,
      });
      const busted =
        (res.image_url || `/api/events/${eventDbId}/risk-snapshot`) +
        `?t=${Date.now()}`;
      patchEventImageUrl(String(payload?.uiEventId || eventDbId), busted, eventDbId);
      setBboxesDirty(false);
      return busted;
    },
    [buildAlertSnapshotPayload, bboxesForExport, patchEventImageUrl, payload?.uiEventId]
  );

  /** 이벤트 ID 없을 때 스테이징 폴더에 캡처·bbox 저장 */
  const persistStagedSnapshot = useCallback(
    async (snap: CentralRiskEventPayload): Promise<boolean> => {
      const cctvId = snap.cctvId;
      const triggerTs = snap.triggerTs;
      if (cctvId == null || triggerTs == null) return false;
      const snapPayload = await buildAlertSnapshotPayload();
      if (!snapPayload.snapshotBase64) return false;
      await eventService.uploadStagedEvidenceSnapshot({
        poolId: snap.poolId,
        cctvId: Number(cctvId),
        triggerTs: Number(triggerTs),
        snapshotBase64: snapPayload.snapshotBase64,
        snapshotRawBase64: snapPayload.snapshotRawBase64,
        bboxes: bboxesForExport,
      });
      stagingSnapshotKeptRef.current = true;
      setBboxesDirty(false);
      return true;
    },
    [buildAlertSnapshotPayload, bboxesForExport]
  );

  // 이벤트 ID가 생긴 뒤 bbox를 고치면 자동 저장 (수동 저장 누락·덮어쓰기 방지)
  useEffect(() => {
    if (!open || !bboxesDirty || payload?.eventDbId == null) return;
    if (bboxSaveBusy || actionBusy) return;
    const eventDbId = payload.eventDbId;
    const snap = payload;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          await persistEditedSnapshot(eventDbId);
          if (snap.cctvId != null && snap.triggerTs != null) {
            await persistStagedSnapshot(snap);
          }
          setBboxSaveNote('캡처 bbox 자동 저장됨');
        } catch (e) {
          console.warn('bbox auto-save failed', e);
        }
      })();
    }, 1200);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dirty 편집 시에만
  }, [open, bboxesDirty, editBboxes, payload?.eventDbId]);

  const handleSaveBboxes = async () => {
    if (!payload) return;
    setBboxSaveBusy(true);
    setBboxSaveNote(null);
    try {
      let saved = false;
      if (payload.eventDbId != null) {
        const url = await persistEditedSnapshot(payload.eventDbId);
        if (!url) {
          setBboxSaveNote('캡처 이미지 없음');
          return;
        }
        saved = true;
      }
      if (payload.cctvId != null && payload.triggerTs != null) {
        try {
          await persistStagedSnapshot(payload);
          saved = true;
        } catch {
          /* event 저장이 됐으면 스테이징 실패는 경고만 */
        }
      }
      if (!saved) {
        setBboxSaveNote(
          '캡처 저장에 실패. CCTV·감지시각 또는 이벤트 ID를 확인.'
        );
        return;
      }
      setBboxSaveNote(
        payload.eventDbId != null
          ? '캡처 bbox를 저장함. 이벤트 목록에도 반영됨'
          : '캡처 bbox를 임시 저장함. 알림 전송 시 이벤트에 반영됨.'
      );
    } catch (e: unknown) {
      setBboxSaveNote(formatApiErrorMessage(e, '캡처 저장에 실패'));
    } finally {
      setBboxSaveBusy(false);
    }
  };

  const handleCloseEvent = (mode: 'stop' | 'facility_ack' = 'stop') => {
    if (actionBusy || !payload) return;
    setActionBusy(true);
    setAlertError(null);
    setAlertOpen(false);

    const snap = { ...payload };
    const shouldDiscard =
      !alertPersistedRef.current && !stagingSnapshotKeptRef.current;
    const eventDbId = payload.eventDbId;
    const shouldPersistBboxes = bboxesDirty;
    const editSnapshotCopy = bboxesForExport.map((b) => ({
      ...b,
      bbox: [...(b.bbox || [])],
    }));
    const frozenCopy = frozenSnapshotRef.current;
    const keepForFacility = mode === 'facility_ack';

    // 캡처 저장 API가 느려도 팝업은 즉시 닫음 (오탐 버튼과 동일)
    finishClose({ keepDevicesForFacility: keepForFacility });

    void (async () => {
      try {
        if (shouldPersistBboxes) {
          try {
            if (eventDbId != null) {
              const snapPayload = await (async () => {
                const raw =
                  frozenCopy?.trim() || snap.imageDataUrl?.trim() || '';
                if (!raw) return null;
                try {
                  const composed =
                    editSnapshotCopy.length > 0
                      ? await composeEventSnapshotWithBboxes(raw, editSnapshotCopy)
                      : raw;
                  return {
                    snapshotBase64:
                      dataUrlToBase64(composed) ||
                      (composed.startsWith('data:') ? undefined : composed),
                    snapshotRawBase64:
                      dataUrlToBase64(raw) ||
                      (raw.startsWith('data:') ? undefined : raw),
                  };
                } catch {
                  const snapshotBase64 =
                    dataUrlToBase64(raw) ||
                    (raw.startsWith('data:') ? undefined : raw);
                  return {
                    snapshotBase64,
                    snapshotRawBase64: snapshotBase64,
                  };
                }
              })();
              if (snapPayload?.snapshotBase64) {
                const res = await eventService.updateRiskSnapshot({
                  eventId: eventDbId,
                  snapshotBase64: snapPayload.snapshotBase64,
                  snapshotRawBase64: snapPayload.snapshotRawBase64,
                  bboxes: editSnapshotCopy,
                });
                const busted =
                  (res.image_url || `/api/events/${eventDbId}/risk-snapshot`) +
                  `?t=${Date.now()}`;
                patchEventImageUrl(
                  String(snap.uiEventId || eventDbId),
                  busted,
                  eventDbId
                );
              }
            } else if (snap.cctvId != null && snap.triggerTs != null) {
              const raw =
                frozenCopy?.trim() || snap.imageDataUrl?.trim() || '';
              if (raw) {
                let composed = raw;
                try {
                  if (editSnapshotCopy.length > 0) {
                    composed = await composeEventSnapshotWithBboxes(
                      raw,
                      editSnapshotCopy
                    );
                  }
                } catch {
                  composed = raw;
                }
                await eventService.uploadStagedEvidenceSnapshot({
                  poolId: snap.poolId,
                  cctvId: Number(snap.cctvId),
                  triggerTs: Number(snap.triggerTs),
                  snapshotBase64:
                    dataUrlToBase64(composed) ||
                    (composed.startsWith('data:') ? composed : composed),
                  snapshotRawBase64:
                    dataUrlToBase64(raw) ||
                    (raw.startsWith('data:') ? raw : raw),
                  bboxes: editSnapshotCopy,
                });
                stagingSnapshotKeptRef.current = true;
              }
            }
          } catch (e) {
            console.warn('bbox auto-save on close failed', e);
          }
        }
        const stillDiscard =
          shouldDiscard && !stagingSnapshotKeptRef.current;
        if (stillDiscard) await discardStagingInBackground(snap);
        await ackEventInBackground(
          snap,
          false,
          keepForFacility
            ? `관제 확인(시설 확인 대기 · 경광 최대 ${FACILITY_ACK_DEVICE_SEC}초)`
            : '관제 확인(닫기)'
        );
      } catch (e) {
        console.error(e);
      }
    })();
  };

  if (!payload) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          // 배경/ESC → 즉시 경광 OFF (시설 대기는 버튼으로만)
          if (!actionBusy && !alertOpen) void handleCloseEvent('stop');
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobileDialog}
        sx={{ zIndex: CENTRAL_RISK_DIALOG_Z }}
      >
        <DialogTitle>위험 이벤트 — {payload.poolName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {payload.location}
            {effectiveRiskLabel ? ` · ${effectiveRiskLabel}` : ''}
            {applyTargetIndex != null ? ` · 선택 #${applyTargetIndex + 1}` : ''}
          </Typography>
          <TextField
            label="이벤트 메시지"
            value={editEventMessage}
            onChange={(e) => {
              eventMessageTouchedRef.current = true;
              setEditEventMessage(e.target.value);
            }}
            fullWidth
            multiline
            minRows={4}
            sx={{ mb: 1.5 }}
            helperText="자동 생성 문구와 bbox 내역을 한곳에서 확인하고 수정 가능."
          />
          {snapshotUrlForEdit ? (
            <Box sx={{ mb: 1 }}>
              <EditableSnapshotBboxEditor
                imageUrl={
                  editSnapshotUrl?.startsWith('data:')
                    ? editSnapshotUrl
                    : snapshotUrlForEdit.startsWith('data:')
                      ? snapshotUrlForEdit
                      : editSnapshotUrl || snapshotUrlForEdit
                }
                bboxes={editBboxes}
                onChange={(next) => {
                  setEditBboxes(next);
                  setBboxesDirty(true);
                  setBboxSaveNote(null);
                  // 이벤트 종류 선택·박스 편집 시 본문 bbox 내역 즉시 반영
                  setEditEventMessage((prev) =>
                    syncMessageWithBboxDetails(prev, next, payload?.message)
                  );
                }}
                selectedIndex={selectedBboxIndex}
                onSelectedIndexChange={(idx) => {
                  setSelectedBboxIndex(idx);
                  if (idx != null && editBboxes[idx]) {
                    const b = editBboxes[idx];
                    setEditRiskLabel(String(b.riskSubtypeLabel || ''));
                    setEditRiskDescription(String(b.riskDescription || ''));
                    if (b.borderColor) setEditBboxColor(b.borderColor);
                  }
                }}
                scenarioProfile={payload.scenarioProfile}
                detectionScenario={payload.detectionScenario}
                maxHeight={360}
                onEventTypeApplied={(info) => {
                  setEditRiskLabel(info.label);
                  setEditBboxColor(info.color);
                  setBboxSaveNote(
                    `이벤트 종류「${info.label}」을(를) 선택 박스에 적용함`
                  );
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                py: 6,
                textAlign: 'center',
                bgcolor: 'grey.900',
                color: 'grey.400',
                borderRadius: 1,
                mb: 1,
              }}
            >
              <Typography variant="body2">이 시점 영상 불러오기 불가</Typography>
            </Box>
          )}
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              bbox 내역 {editBboxes.length > 0 ? `(${editBboxes.length})` : ''}
            </Typography>
            {editBboxes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                아직 bbox가 없음. 캡처 위에 드래그해 추가
              </Typography>
            ) : (
              <List dense disablePadding sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                {editBboxes.map((b, idx) => {
                  const title =
                    formatBboxOverlayLabel(b) ||
                    `박스 #${idx + 1}`;
                  const desc = String(b.riskDescription || '').trim();
                  const code = String(b.riskSubtypeLabel || '').trim();
                  const color =
                    String(b.borderColor || '').trim() ||
                    (b.motionRisk ? '#ff1744' : '#90caf9');
                  return (
                    <ListItemButton
                      key={`bbox-hist-${b.trackId ?? idx}`}
                      selected={selectedBboxIndex === idx}
                      onClick={() => {
                        setSelectedBboxIndex(idx);
                        setEditRiskLabel(code);
                        setEditRiskDescription(desc);
                        if (b.borderColor) setEditBboxColor(b.borderColor);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 0.5,
                            bgcolor: color,
                            border: '1px solid rgba(0,0,0,0.25)',
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${idx + 1}) ${title}`}
                        secondary={
                          code && desc && title !== `${code} — ${desc}`
                            ? desc
                            : undefined
                        }
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
            {bboxHistoryLines ? (
              <Typography
                variant="caption"
                color="text.secondary"
                component="pre"
                sx={{ mt: 1, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
              >
                {bboxHistoryLines}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                label="코드명"
                placeholder="위험 코드명"
                value={editRiskLabel}
                onChange={(e) => setEditRiskLabel(e.target.value)}
                sx={{ width: 240, flexShrink: 0 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <input
                  type="color"
                  value={
                    /^#[0-9a-fA-F]{6}$/.test(effectiveBboxColor)
                      ? effectiveBboxColor
                      : '#ff1744'
                  }
                  onChange={(e) => setEditBboxColor(e.target.value)}
                  style={{ width: 36, height: 36, border: 'none', padding: 0, cursor: 'pointer' }}
                  aria-label="bbox 색"
                  title="bbox 색"
                />
                <TextField
                  size="small"
                  label="색"
                  value={editBboxColor}
                  onChange={(e) => setEditBboxColor(e.target.value)}
                  placeholder="#ff1744"
                  sx={{ width: 96 }}
                />
              </Box>
              <Button
                size="small"
                variant="contained"
                disabled={editBboxes.length === 0}
                onClick={() => {
                  applyCodeNameAndColor();
                  if (effectiveRiskLabel) {
                    upsertRiskAppearanceEntry(effectiveRiskType, {
                      label: effectiveRiskLabel,
                      color: effectiveBboxColor,
                    });
                  }
                }}
                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                코드명 적용
              </Button>
            </Box>
            <TextField
              size="small"
              label="설명"
              placeholder="현장 전달용 설명 (알림 메시지에 포함)"
              value={editRiskDescription}
              onChange={(e) => setEditRiskDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
          {(autoDispatchBusy || autoDispatchNote) && (
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                color: autoDispatchNote?.includes('실패') || autoDispatchNote?.includes('없어')
                  ? 'error.main'
                  : 'success.main',
              }}
            >
              {autoDispatchBusy ? '자동 알림 발송 중…' : autoDispatchNote}
            </Typography>
          )}
          {snapshotUrlForEdit ? (
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => void handleSaveBboxes()}
                disabled={bboxSaveBusy || actionBusy || !snapshotUrlForEdit}
              >
                {bboxSaveBusy ? '저장 중…' : '캡처 bbox 저장'}
              </Button>
              {bboxesDirty ? (
                <Typography variant="caption" color="warning.main">
                  수정됨 — 저장 버튼을 누르거나 닫으면 반영됨
                </Typography>
              ) : null}
              {bboxSaveNote ? (
                <Typography
                  variant="caption"
                  color={bboxSaveNote.includes('실패') ? 'error' : 'success.main'}
                >
                  {bboxSaveNote}
                </Typography>
              ) : null}
              {payload.eventDbId == null && !bboxSaveNote && !bboxesDirty ? (
                <Typography variant="caption" color="text.secondary">
                  이벤트 생성 전이면 임시 저장됨. 알림 전송 시 이벤트에 연결됨.
                </Typography>
              ) : null}
            </Box>
          ) : null}

          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={includeSnapshotWithAlert}
                onChange={(_e, checked) => setIncludeSnapshotWithAlert(checked)}
                disabled={!snapshotUrlForEdit || actionBusy || autoDispatchBusy}
              />
            }
            label={
              <Box>
                <Typography variant="body2">알림·이벤트에 캡처 이미지 포함</Typography>
                <Typography variant="caption" color="text.secondary">
                  {snapshotUrlForEdit
                    ? '기본 ON — 시설 팝업·이벤트·발송 이력에 bbox 반영 캡처 첨부'
                    : '표시할 캡처가 없어 사용 불가'}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 1 }}
          />
          <EventAlertDeviceControls
            poolId={poolIdNumeric}
            deviceType="siren"
            title="경광등"
            enabled={open}
            onTargetChange={(t) => {
              sirenTargetRef.current = t || '';
            }}
            onDeviceCountChange={(n) => {
              sirenDeviceCountRef.current = n;
            }}
          />
          <EventAlertDeviceControls
            poolId={poolIdNumeric}
            deviceType="buzzer"
            title="부저"
            enabled={open}
            onTargetChange={(t) => {
              buzzerTargetRef.current = t || '';
            }}
            onDeviceCountChange={(n) => {
              buzzerDeviceCountRef.current = n;
            }}
          />
          <Button
            size="small"
            color="warning"
            variant="outlined"
            onClick={() => stopAlertDevicesNow()}
            disabled={poolIdNumeric == null || actionBusy}
            sx={{ mb: 1 }}
          >
            경광등/부저 울림 해제
          </Button>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
          <Button
            color="inherit"
            onClick={() => void handleCloseEvent('stop')}
            disabled={actionBusy}
            title="이벤트 처리완료 + 경광등/부저 즉시 중지"
          >
            닫기 (처리완료)
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => void handleCloseEvent('facility_ack')}
            disabled={actionBusy}
            title={`창만 닫고 경광/부저는 시설 「닫기」까지 유지 (최대 ${FACILITY_ACK_DEVICE_SEC}초 후 자동 중지)`}
          >
            닫기 (시설 확인 대기)
          </Button>
          <Button color="warning" onClick={handleFalsePositive} disabled={actionBusy}>
            오탐
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => void openAlertPreview('warning')}
            disabled={actionBusy}
          >
            경고알림
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void openAlertPreview('danger')}
            disabled={actionBusy}
          >
            위험알림
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={alertOpen}
        onClose={closeAlertDialog}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: CENTRAL_RISK_DIALOG_Z + 2 }}
      >
        <DialogTitle>{previewTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 0.5, mb: 1 }}>
            전송 채널
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            시설 정보의 「현장 안전 알림 채널」 설정과 같음. OFF인 매체는 선택 불가.
          </Typography>
          {channelsPrefsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                시설 채널 설정 불러오는 중…
              </Typography>
            </Box>
          ) : (
          <FormGroup row sx={{ mb: 1, gap: 1 }}>
            {(['sms', 'kakao', 'wearable'] as const).map((key) => {
              const allowed = poolChannelAllowed[key];
              const label = SAFETY_ALERT_CHANNEL_LABELS[key];
              return (
                <FormControlLabel
                  key={key}
                  disabled={!allowed}
                  control={
                    <Checkbox
                      checked={allowed ? channels[key] : false}
                      disabled={!allowed}
                      onChange={(e) => setChannelChecked(key, e.target.checked)}
                    />
                  }
                  label={allowed ? label : `${label} (시설 설정 OFF)`}
                />
              );
            })}
          </FormGroup>
          )}
          {((poolChannelAllowed.sms && channels.sms) ||
            (poolChannelAllowed.kakao && channels.kakao)) && (
            <TextField
              fullWidth
              label="수신 번호 (쉼표 구분, 비우면 시설 등록 번호)"
              value={alertPhones}
              onChange={(e) => setAlertPhones(e.target.value)}
              margin="normal"
              placeholder="01012345678, 01098765432"
            />
          )}
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
            경광등 / 부저 (전송 시 함께)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            체크 후 전송하면 울림. 「울림 해제」 또는 이벤트 창을 닫으면 중지됨.
          </Typography>
          <FormGroup row sx={{ mb: 1, gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={alertSirenOn}
                  onChange={(e) => setAlertSirenOn(e.target.checked)}
                  disabled={alertSending}
                />
              }
              label="경광등 울림"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={alertBuzzerOn}
                  onChange={(e) => setAlertBuzzerOn(e.target.checked)}
                  disabled={alertSending}
                />
              }
              label="부저 울림"
            />
          </FormGroup>
          <TextField
            fullWidth
            label="메시지"
            value={alertText}
            onChange={(e) => {
              setAlertText(e.target.value);
              setEditEventMessage(e.target.value);
              eventMessageTouchedRef.current = true;
            }}
            margin="normal"
            multiline
            minRows={4}
          />
          {snapshotUrlForEdit ? (
            <FormControlLabel
              sx={{ mt: 1, alignItems: 'flex-start' }}
              control={
                <Switch
                  checked={includeSnapshotWithAlert}
                  onChange={(_e, checked) => setIncludeSnapshotWithAlert(checked)}
                  disabled={alertSending}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">캡처 이미지 함께 저장</Typography>
                  <Typography variant="caption" color="text.secondary">
                    전송 시 시설 팝업·이벤트·발송 이력에 bbox 반영 캡처 포함(기본 ON).
                  </Typography>
                </Box>
              }
            />
          ) : null}
          {alertError ? (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {alertError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAlertDialog} disabled={alertSending}>
            취소
          </Button>
          <Button
            variant="contained"
            color={alertLevel === 'danger' ? 'error' : 'warning'}
            onClick={() => void handleSendAlert()}
            disabled={alertSending || channelsPrefsLoading}
            startIcon={alertSending ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            전송
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CentralRiskEventDialog;
